<template>
  <div>
    <v-text-field
      v-if="includeSearch"
      ref="input"
      v-model="conditions.search"
      :label="$t('Search')"
      autofocus
      class="mb-6"
      clearable
      filled
      hide-details
      prepend-inner-icon="far fa-search"
      single-line
    />

    <v-dialog
      v-model="showAddDialog"
      eager
      max-width="800px"
    >
      <forms-customers-save
        v-if="showAddDialog"
        ref="dialogAddCustomer"
        :focus-first-name="showAddDialog"
        :form-title="$t('Add {0}', [$t('Customer')])"
        @saved="itemAdded($event)"
      />
    </v-dialog>

    <v-dialog
      v-model="showEditDialog"
      max-width="800px"
      eager
    >
      <forms-customers-save
        v-if="selectedCustomer"
        ref="dialogEditCustomer"
        :customer-object="selectedCustomer"
        :focus-first-name="showEditDialog"
        :form-title="$t('Edit {0}', [$t('Customer')])"
        @saved="itemSaved($event)" />
    </v-dialog>

    <v-data-table
      v-bind="dataTableProps"
      :headers="headers"
      :items="customers"
      :loading="$wait.is('fetching customers')"
      :options.sync="pagination"
      :server-items-length="nrOfCustomers"
      class="mb-10"
    >
      <template v-slot:item="props">
        <tr
          style="cursor: pointer"
          @click="itemSelected(props.item)"
        >
          <td class="pt-2">
            <flag
              :locale="props.item.language"
              width="20px"
            />
          </td>
          <td>{{ props.item.fullName }}</td>
          <td class="d-none">{{ props.item.address }}</td>
          <td>{{ props.item.email }}</td>
          <td>{{ props.item.city }}</td>
          <td>{{ props.item.phone }}</td>
          <td>{{ props.item.created | moment("D MMMM YYYY") }}</td>
          <td class="text-right">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  class="ma-0"
                  icon
                  small
                  v-on="on"
                  @click.stop="editItem(props.item)"
                >
                  <v-icon small>edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Edit') }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  class="ma-0"
                  icon
                  small
                  v-on="on"
                  @click.stop="showPurchases(props.item)"
                >
                  <v-icon small>fal fa-calendar-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Sales History') }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-fab-transition>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-show="!showAddDialog"
            bottom
            color="secondary darken-1"
            dark
            fab
            fixed
            right
            v-on="on"
            @click="addItem"
          >
            <v-icon>fal fa-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('Add {0}', [$t('Customer')]) }}</span>
      </v-tooltip>
    </v-fab-transition>

    <v-dialog
      v-model="showCustomerReceiptsDialog"
      eager
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card
        v-if="selectedCustomer"
        tile
      >
        <v-app-bar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>
            <v-icon class="mr-2">
              fal fa-user
            </v-icon>
            {{ $t('Sales History') }}: {{ selectedCustomer.fullName }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              dark
              @click.native="showCustomerReceiptsDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-app-bar>
        <v-container
          fluid
          grid-list-md
        >
          <receipts-table
            v-if="selectedCustomer"
            :customer-object="selectedCustomer"
            :include-speed-dial="false"
            include-search
          />
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import customerService from '@/services/customers'
import camelcaseKeys from 'camelcase-keys'
import focusInputMixin from '@/utils/mixins/focusInputMixin'
import FormsCustomersSave from '@/components/forms/customers/FormsCustomersSave'
// import ReceiptsTable from '@/components/receipts/ReceiptsTable'
import { VDataTableDefaultProps as dataTableProps } from '@/utils/default-props/v-data-table'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import Flag from '@/components/Flag'

export default {
  name: 'CustomersTable',

  components: {
    FormsCustomersSave,
    // ReceiptsTable,
    Flag
  },

  mixins: [ focusInputMixin ],

  props: {
    includeSearch: {
      default: false,
      type: Boolean
    }
  },

  data () {
    return {
      showCustomerReceiptsDialog: false,
      activeDialogTab: 'tab-main',
      icon: 'fal fa-plus',
      headers: [
        {
          text: this.$t('Language'),
          value: 'language',
          width: '120px'
        },
        {
          text: this.$t('Name'),
          value: 'lastName'
        },
        {
          text: this.$t('Address'),
          value: 'address',
          class: 'd-none'
        },
        {
          text: this.$t('Email Address'),
          value: 'email'
        },
        {
          text: this.$t('City'),
          value: 'city'
        },
        {
          text: this.$t('Phone'),
          value: 'phone'
        },
        {
          text: this.$t('Since'),
          value: 'created',
          filterable: false
        },
        {
          text: '',
          value: 'id',
          sortable: false,
          filterable: false,
          width: 150
        }
      ],
      search: '',
      scores: {},
      customers: [],
      conditions: {},
      pagination: {},
      nrOfCustomers: 0,
      showAddDialog: false,
      showEditDialog: false,
      selectedCustomer: null,
      dataTableProps
    }
  },

  watch: {
    showAddDialog (value) {
      // modal is closing
      !value && this.$refs.dialogAddCustomer.resetActiveTab()
    },
    showEditDialog (value) {
      // modal is closing
      !value && this.$refs.dialogEditCustomer.resetActiveTab()
    },
    // fetch receipts when requested page or sorting changed
    pagination (value) {
      this.updateResults()
    },
    // fetch receipts when search has changed
    'conditions.search': function (search) {
      this.updateResultsDebounced()
    }
  },

  created () {
    // update the datatable items when store state has changed (async call in App.vue created)
    this.$store.watch(
      (state) => {
        return state.customers
      },
      (customers) => {
        this.customers = this.filteredCustomers = customers
      },
      {
        immediate: true
      }
    )
  },

  methods: {
    addItem () {
      this.showAddDialog = true
    },
    editItem (item) {
      this.selectedCustomer = item
      this.showEditDialog = true
    },
    itemAdded (item) {
      this.showAddDialog = false
      this.$emit('item-saved', item)
    },
    itemSelected (item) {
      this.$emit('item-selected', item)
      // Click on table row should open edit popup on customers overview
      if (this.$route.name === 'CustomersIndex') {
        this.editItem(item)
      }
    },
    getPhoneWithoutCountryCode (phone) {
      const parsed = parsePhoneNumberFromString(phone)
      if (parsed) {
        return `0${parsed.nationalNumber}`
      }
      return phone
    },
    itemSaved (item) {
      this.showEditDialog = false
      this.$emit('item-saved', item)
    },
    showPurchases (item) {
      this.selectedCustomer = item
      this.showCustomerReceiptsDialog = true
    },
    updateResults () {
      this.$wait.start('fetching customers')
      // reset dataset
      this.receipts = []

      customerService.pagination(this.conditions, this.pagination)
        .then(data => {
          if (data.data) {
            this.customers = camelcaseKeys(data.data.customers, { deep: true })
            this.nrOfCustomers = data.data.total_count
          }
        })
        .finally(data => this.$wait.end('fetching customers'))
    },
    updateResultsDebounced: _.debounce(function () {
      this.updateResults()
    }, 500),
    resetToFirstPage () {
      if (this.pagination) {
        this.$set(this.pagination, 'page', 1)
        this.updateResults()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/customers/CustomersTable.vue